import { NgModule, inject, APP_INITIALIZER } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { TMA_ICONS, TmaIconDef } from './tma-icons.registry';

/**
 * ============================================================================
 * TMA ICONS MODULE
 * ============================================================================
 * Registers all TMA custom icons with Angular Material's MatIconRegistry
 * under the 'tma' namespace. After importing this module, use icons via:
 *
 *   <mat-icon svgIcon="tma:patient"></mat-icon>
 *   <mat-icon svgIcon="tma:physician" class="icon-size-5"></mat-icon>
 *   <mat-icon svgIcon="tma:tminsights"></mat-icon>
 *   <mat-icon svgIcon="tma:tmai"></mat-icon>
 *
 * In navigation service (navigation.service.ts):
 *   icon: 'tma:patient'
 *   icon: 'tma:physician'
 *   icon: 'tma:referring-physician'
 *
 * DUAL STATE (outline → solid on active):
 *   Default nav state  → uses icon.outline paths
 *   Active nav state   → swap to solid via CSS class or component logic:
 *
 *   [svgIcon]="isActive ? 'tma:' + name + '-solid' : 'tma:' + name"
 *
 *   Or register both variants (done automatically below):
 *   'tma:patient'        → outline version
 *   'tma:patient-solid'  → solid/filled version
 *
 * ICON SIZES (Fuse Tailwind classes):
 *   class="icon-size-4"  → 16px  (dense lists)
 *   class="icon-size-5"  → 20px  (nav sidebar — recommended)
 *   class="icon-size-6"  → 24px  (section headers, cards)
 *   class="icon-size-8"  → 32px  (empty states, hero)
 * ============================================================================
 */

function buildSvg(innerSvg: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">${innerSvg}</svg>`;
}

function registerTmaIcons(
  registry: MatIconRegistry,
  sanitizer: DomSanitizer
): void {
  TMA_ICONS.forEach((icon: TmaIconDef) => {
    // Register outline (default) — accessible as 'tma:icon-name'
    registry.addSvgIconLiteralInNamespace(
      'tma',
      icon.name,
      sanitizer.bypassSecurityTrustHtml(buildSvg(icon.outline))
    );
    // Register solid variant — accessible as 'tma:icon-name-solid'
    registry.addSvgIconLiteralInNamespace(
      'tma',
      `${icon.name}-solid`,
      sanitizer.bypassSecurityTrustHtml(buildSvg(icon.solid))
    );
  });
}

@NgModule({
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const registry = inject(MatIconRegistry);
        const sanitizer = inject(DomSanitizer);
        return () => registerTmaIcons(registry, sanitizer);
      },
      multi: true,
    },
  ],
})
export class TmaIconsModule {}
